import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X, ShoppingBag, ArrowUpRight, ShieldCheck, Package, Droplets } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { MoleculeThumb } from "@/components/MoleculeThumb";
import { fetchProductsLite } from "@/lib/api";

export default function Cart() {
    const { cart, updateQty, removeFromCart, formatPrice, cartTotalUsd, cartFinalUsd, bundleDiscount, firstOrderHint, firstOrderDiscountUsd, cartFinalAfterFirstOrderUsd, FIRST_ORDER_PCT, addToCart, bacBundleActive, effectiveItemPriceUsd } = useApp();
    const navigate = useNavigate();
    const [bacProduct, setBacProduct] = React.useState(null);

    // Lazy-load the BAC water product so we can offer a one-click bundled add.
    React.useEffect(() => {
        let cancelled = false;
        if (cart.length > 0 && !cart.some((i) => i.product_id === "bac-water-10ml") && !bacProduct) {
            fetchProductsLite().then((d) => {
                if (cancelled) return;
                const list = Array.isArray(d) ? d : (d.products || []);
                setBacProduct(list.find((p) => p.id === "bac-water-10ml") || null);
            }).catch(() => {});
        }
        return () => { cancelled = true; };
    }, [cart.length, bacProduct]);

    // Show the BAC bundle upsell when at least one non-BAC item is in cart and BAC isn't already present.
    const hasBacInCart = cart.some((i) => i.product_id === "bac-water-10ml");
    const showBacUpsell = bacProduct && !hasBacInCart && cart.some((i) => i.product_id !== "bac-water-10ml");

    // Made-to-order surcharge, flat $50 once when any MTO item is in cart (mirrors backend logic).
    const mtoItems = cart.filter((i) => i.made_to_order);
    const hasMto = mtoItems.length > 0;
    const mtoFeeUsd = hasMto ? Math.max(...mtoItems.map((i) => Number(i.made_to_order_fee_usd) || 50)) : 0;
    const mtoLeadTime = hasMto ? (mtoItems[0].made_to_order_lead_time || "4 to 6 business days") : "";
    const cartFinalWithMto = cartFinalAfterFirstOrderUsd + mtoFeeUsd;

    if (cart.length === 0) {
        return (
            <div className="bg-ink-base min-h-screen">
                <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="cart-empty">
                    <ShoppingBag className="w-12 h-12 mx-auto text-ink-mute" strokeWidth={1.3} />
                    <h1 className="mt-6 font-display text-4xl font-bold text-ink">Cart is empty</h1>
                    <p className="mt-3 text-ink-dim">Explore our research catalog to get started.</p>
                    <Link to="/shop" className="mt-8 inline-flex items-center gap-2 bg-neon hover:bg-neon-glow text-black px-8 py-4 font-bold transition-colors" data-testid="cart-shop-link">
                        ENTER CATALOG <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-ink-base text-ink min-h-screen" data-testid="cart-page">
            <div className="max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12 py-16">
                <p className="section-num mb-4">/ CART · {cart.length} ITEM{cart.length === 1 ? "" : "S"}</p>
                <h1 className="font-display text-5xl sm:text-7xl font-bold text-ink tracking-tight mb-14 leading-[0.92]">Your<br /><span className="text-neon">order.</span></h1>

                <div className="grid md:grid-cols-[1fr_380px] gap-12">
                    <div className="border-t border-ink" data-testid="cart-items">
                        {cart.map((item) => (
                            <div key={item.product_id} className="flex gap-4 py-6 border-b border-ink" data-testid={`cart-item-${item.product_id}`}>
                                <Link to={`/product/${item.slug}`} className="w-24 h-24 sm:w-32 sm:h-32 border border-ink bg-ink-surface shrink-0 overflow-hidden">
                                    <MoleculeThumb molecule={item.molecule} className="w-full h-full" />
                                </Link>
                                <div className="flex-1 flex flex-col">
                                    <div className="flex justify-between gap-4">
                                        <div>
                                            <h3 className="font-display text-lg font-bold text-ink">{item.name}</h3>
                                            <p className="font-mono text-xs text-ink-mute mt-0.5">{item.variant_label || item.dosage}</p>
                                        </div>
                                        <button onClick={() => removeFromCart(item.product_id)} className="text-ink-mute hover:text-red-500 transition-colors" data-testid={`remove-${item.product_id}`} aria-label="Remove">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <div className="flex items-end justify-between mt-auto pt-4">
                                        <div className="flex items-center border border-ink-strong">
                                            <button onClick={() => updateQty(item.product_id, item.quantity - 1)} className="px-2.5 py-2 text-ink-dim hover:text-neon transition-colors" data-testid={`cart-qty-dec-${item.product_id}`} aria-label="Decrease"><Minus className="w-3.5 h-3.5" /></button>
                                            <span className="px-4 py-2 font-mono text-sm font-semibold text-ink min-w-[40px] text-center">{item.quantity}</span>
                                            <button onClick={() => updateQty(item.product_id, item.quantity + 1)} className="px-2.5 py-2 text-ink-dim hover:text-neon transition-colors" data-testid={`cart-qty-inc-${item.product_id}`} aria-label="Increase"><Plus className="w-3.5 h-3.5" /></button>
                                        </div>
                                        <div className="text-right">
                                            {item.product_id === "bac-water-10ml" && bacBundleActive && (
                                                <div className="text-[10px] font-mono text-gold uppercase tracking-wider mb-0.5" data-testid="cart-bac-bundle-tag">★ BUNDLED · −${(item.price_usd - 10).toFixed(2)}/ea</div>
                                            )}
                                            <span className="font-display text-lg font-bold text-ink">{formatPrice(effectiveItemPriceUsd(item) * item.quantity)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {showBacUpsell && (
                            <div className="my-6 p-5 border border-gold/40 bg-gold/5 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="bac-bundle-upsell">
                                <div className="w-12 h-12 rounded-full bg-gold/15 flex items-center justify-center shrink-0">
                                    <Droplets className="w-5 h-5 text-gold" strokeWidth={1.75} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-display text-base font-bold text-ink leading-tight">Add 10mL BAC Water, bundled at <span className="text-gold">$10</span> <span className="text-ink-mute font-mono text-xs line-through ml-1">$19</span></p>
                                    <p className="text-xs text-ink-dim mt-1 leading-relaxed">All lyophilized peptides need bacteriostatic water for reconstitution. Save $9 when added with any peptide.</p>
                                </div>
                                <button
                                    onClick={() => addToCart({ ...bacProduct, price_usd: bacProduct.price_usd }, 1)}
                                    className="bg-gold hover:bg-gold/90 text-black px-5 py-2.5 text-xs font-bold uppercase tracking-wider whitespace-nowrap"
                                    data-testid="bac-bundle-add-btn">
                                    + ADD FOR $10
                                </button>
                            </div>
                        )}
                    </div>

                    <aside className="md:sticky md:top-32 md:self-start">
                        <div className="border border-ink-strong p-6 bg-ink-surface" data-testid="cart-summary">
                            <h3 className="overline text-neon mb-6">ORDER SUMMARY</h3>
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between text-ink-dim"><span>Subtotal</span><span className="font-mono text-ink">{formatPrice(cartTotalUsd)}</span></div>
                                {bundleDiscount.discount_usd > 0 && bundleDiscount.applied.map((a) => (
                                    <div key={a.group} className="flex justify-between text-neon" data-testid={`bundle-savings-${a.group}`}>
                                        <span className="font-mono text-xs">★ {a.label} · {Math.round(a.pct * 100)}% OFF</span>
                                        <span className="font-mono">−{formatPrice(a.discount_usd)}</span>
                                    </div>
                                ))}
                                {firstOrderHint && firstOrderDiscountUsd > 0 && (
                                    <div className="flex justify-between text-gold" data-testid="first-order-discount">
                                        <span className="font-mono text-xs">⌬ FIRST ORDER · {Math.round(FIRST_ORDER_PCT * 100)}% OFF</span>
                                        <span className="font-mono">−{formatPrice(firstOrderDiscountUsd)}</span>
                                    </div>
                                )}
                                {hasMto && (
                                    <div className="flex justify-between text-amber-400" data-testid="mto-fee">
                                        <span className="font-mono text-xs">⚒ MADE-TO-ORDER SHIPPING</span>
                                        <span className="font-mono">+{formatPrice(mtoFeeUsd)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-ink-dim"><span>Shipping</span><span className="font-mono text-neon">At checkout</span></div>
                            </div>
                            <div className="mt-5 pt-5 border-t border-ink flex justify-between items-end">
                                <span className="overline text-ink">TOTAL</span>
                                <div className="text-right">
                                    {(bundleDiscount.discount_usd > 0 || firstOrderDiscountUsd > 0) && (
                                        <div className="text-xs font-mono text-ink-mute line-through">{formatPrice(cartTotalUsd)}</div>
                                    )}
                                    <span className="font-display text-2xl font-bold text-ink" data-testid="cart-total">{formatPrice(cartFinalWithMto)}</span>
                                </div>
                            </div>

                            {hasMto && (
                                <div className="mt-4 p-3 border-2 border-amber-500/50 bg-amber-500/10 text-xs text-amber-400 leading-relaxed" data-testid="mto-banner">
                                    <strong className="font-mono uppercase tracking-wider text-[10px] block mb-1">⚒ Made to order · {mtoLeadTime}</strong>
                                    A flat ${mtoFeeUsd.toFixed(0)} shipping cost is included for made-to-order kits. These are produced fresh from lab stock, expect {mtoLeadTime} of additional production time on top of normal transit.
                                </div>
                            )}

                            {firstOrderHint && firstOrderDiscountUsd > 0 && (
                                <div className="mt-4 p-3 border-l-2 border-gold bg-gold/5 text-xs text-gold leading-relaxed" data-testid="first-order-banner">
                                    <strong className="font-mono uppercase tracking-wider text-[10px] block mb-1">Welcome, first order on us.</strong>
                                    10% off automatically applied. Validated at checkout against your email.
                                </div>
                            )}

                            <button
                                onClick={() => navigate("/checkout")}
                                className="w-full mt-6 bg-neon hover:bg-neon-glow text-black px-6 py-4 font-bold tracking-wide transition-colors inline-flex items-center justify-center gap-2"
                                data-testid="checkout-button"
                            >
                                PROCEED TO CHECKOUT <ArrowUpRight className="w-4 h-4" />
                            </button>

                            <div className="mt-6 space-y-3 text-xs text-ink-dim">
                                <div className="flex items-start gap-2"><ShieldCheck className="w-3.5 h-3.5 text-neon mt-0.5 shrink-0" /><span>Every product Janoshik COA verified</span></div>
                                <div className="flex items-start gap-2"><Package className="w-3.5 h-3.5 text-neon mt-0.5 shrink-0" /><span>Plain unmarked packaging · no labels</span></div>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
}
